import API_BASE_URL from '../config';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './Notifications.css';

interface Notification {
    _id: string;
    title: string;
    message: string;
    type: 'ceremony' | 'task' | 'system';
    isRead: boolean;
    createdAt: string;
}

interface NotificationCenterProps {
    showLabel?: boolean;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ showLabel }) => {
    const { accessToken } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);

    const fetchNotifications = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/notifications`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await response.json();
            if (response.ok) {
                setNotifications(data.notifications);
            }
        } catch (err) {
            console.error('Error fetching notifications');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchNotifications();
            // Refresh every 30 seconds
            const interval = setInterval(fetchNotifications, 30 * 1000);
            return () => clearInterval(interval);
        }
    }, [accessToken]);

    /**
     * Mark ALL unread as read — called immediately when panel opens.
     * Optimistically updates local state so the badge disappears instantly,
     * then fires the API in the background.
     */
    const markAllAsRead = async () => {
        const hasUnread = notifications.some(n => !n.isRead);
        if (!hasUnread) return;

        // Optimistic update — clear badge immediately
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));

        try {
            await fetch(`${API_BASE_URL}/api/notifications/mark-all-read`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        } catch (err) {
            console.error('Error marking all notifications as read');
        }
    };

    const handleToggle = () => {
        const opening = !isOpen;
        setIsOpen(opening);
        if (opening) {
            // Clear the badge the moment the user opens the panel
            markAllAsRead();
        }
    };

    const markAsRead = async (id: string) => {
        try {
            await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setNotifications(notifications.map(n => n._id === id ? { ...n, isRead: true } : n));
        } catch (err) {
            console.error('Error marking as read');
        }
    };

    const deleteNotification = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await fetch(`${API_BASE_URL}/api/notifications/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setNotifications(notifications.filter(n => n._id !== id));
        } catch (err) {
            console.error('Error deleting notification');
        }
    };

    const unreadCount = notifications.filter(n => n.isRead === false).length;

    return (
        <div className="notification-center-container" style={{ zIndex: 100, width: showLabel ? '100%' : 'auto' }}>
            <button
                className={`notification-trigger ${unreadCount > 0 ? 'has-unread' : ''} ${showLabel ? 'with-label' : ''}`}
                onClick={handleToggle}
            >
                <div className="trigger-content">
                    <span className="bell-icon">🔔</span>
                    {showLabel && <span className="trigger-label">Notifications</span>}
                </div>
                {unreadCount > 0 && (
                    <span className={`unread-badge ${showLabel ? 'badge-relative' : ''}`}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown glass-panel">
                    <div className="dropdown-header">
                        <h3>Notifications</h3>
                        <button className="close-dropdown" onClick={() => setIsOpen(false)}>×</button>
                    </div>
                    <div className="notification-list">
                        {notifications.length === 0 ? (
                            <div className="empty-state">No new notifications</div>
                        ) : (
                            notifications.map(n => (
                                <div
                                    key={n._id}
                                    className={`notification-item ${n.isRead ? 'read' : 'unread'}`}
                                    onClick={() => !n.isRead && markAsRead(n._id)}
                                >
                                    <div className="notif-content">
                                        <div className="notif-header">
                                            <span className={`notif-type-tag ${n.type}`}>{n.type}</span>
                                            <span className="notif-time">{new Date(n.createdAt).toLocaleDateString()}</span>
                                        </div>
                                        <h4 className="notif-title">{n.title}</h4>
                                        <p className="notif-message">{n.message}</p>
                                    </div>
                                    <button className="delete-notif" onClick={(e) => deleteNotification(n._id, e)}>🗑️</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
